/**
 * ClassificationChip — universal inline classification surface (H.9.7).
 *
 * One React primitive that wraps every classification display in the app:
 * PO detail, search results, account drilldowns, FlagReview cards, charts,
 * vendor pages, dashboards. Default render = today's chip (name + confidence
 * + method). Click expands to an inline panel with three actions:
 *
 *   1. Apply correction (admin only) — component picker + reason → writes
 *      via POST /api/review/item/:id/classify. Phase 0 hook captures the
 *      decision row with system_proposed=old and final=new.
 *   2. Research with AI — sends prose + optional pick as hint to Sonnet
 *      via POST /api/classification/flags/item/:id/reclassify (useSonnet
 *      true). Returns the agent's proposal in the same panel; admin can
 *      Apply it directly.
 *   3. Just flag — POST /api/classification/flags with the prose. Captures
 *      the insight without committing to a correction. Non-admins also use
 *      this path for "Suggest correction" (permission-aware copy).
 *
 * Permission-aware from day one: admins see "Apply correction"; non-admins
 * see "Suggest correction" (flag with prose). Same schema, same component,
 * different write semantics — when Phase B (multi-contributor) lights up,
 * no UI rebuild is required.
 *
 * Phase 0 capture: every action emits a classification_decision row via
 * the corresponding backend write-hook. The chip itself doesn't speak to
 * the decision table — it just calls existing routes, and the routes are
 * already instrumented as of migration 1786200000000.
 */

import React, { useState } from 'react';
import {
  Tag, X, Sparkles, Flag, Check, Loader2, AlertCircle, Edit3,
} from 'lucide-react';
import { useMutation } from '@tanstack/react-query';
import apiClient from '../../lib/apiClient';
import { useRole } from '../../hooks/useRole';
import SearchableDropdown from '../ClassificationIntelligence/SearchableDropdown';
import './ClassificationChip.css';

// Confidence → 0-100 integer percent. Returns null when confidence is
// missing or non-numeric (e.g. unclassified items). Exported because other
// surfaces (multi-chip aggregates in TransactionsDrilldownModal) need the
// same band thresholds — keep the source of truth here.
export function confidenceToPct(confidence) {
  if (confidence == null) return null;
  const num = Number(confidence);
  if (!Number.isFinite(num)) return null;
  return Math.round(num * 100);
}

// Semantic band for color treatment. Thresholds align with the chip CSS
// data-band selectors and the graduation precision targets in the North
// Star (≥99% autonomous, ≥85% trusted, ≥60% needs-review).
export function confidenceBand(pct) {
  if (pct == null) return 'unclassified';
  if (pct >= 85) return 'high';
  if (pct >= 60) return 'mid';
  return 'low';
}

function ConfidenceBadge({ pct, band }) {
  if (pct == null) return null;
  return <span className={`cc-conf cc-conf--${band}`}>{pct}%</span>;
}

export default function ClassificationChip({
  itemId,
  componentName,
  confidence,
  classificationMethod,
  size = 'sm',
  onUpdated,
  // Optional explicit override for the trigger. When falsy, the chip
  // renders its own pill button. Some consumers (e.g. dense tables) may
  // prefer to render their own label and pass children — we keep that
  // flexibility but default to the pill so callers don't have to think.
  className = '',
}) {
  const { isAdmin } = useRole();
  const [open, setOpen] = useState(false);
  const [hint, setHint] = useState('');
  const [pickedComponent, setPickedComponent] = useState(null);
  const [aiProposal, setAiProposal] = useState(null);
  const [aiError, setAiError] = useState(null);
  const [submitError, setSubmitError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);

  function resetState() {
    setHint('');
    setPickedComponent(null);
    setAiProposal(null);
    setAiError(null);
    setSubmitError(null);
    setSuccessMsg(null);
  }

  function closeAndReset() {
    setOpen(false);
    resetState();
  }

  // ── Mutations ───────────────────────────────────────────────────────
  // Each mutation maps to one of the three operator intents. The Phase 0
  // capture lives on the backend (`classification_decision` write-hooks),
  // so the chip only worries about UX state — success/error per intent.

  const applyManual = useMutation({
    mutationFn: ({ componentId, notes }) =>
      apiClient.post(`/api/review/item/${itemId}/classify`, {
        componentId,
        notes,
        confidence: 0.95,
      }),
    onSuccess: () => {
      setSuccessMsg('Correction applied.');
      setTimeout(() => {
        closeAndReset();
        onUpdated?.();
      }, 800);
    },
    onError: (err) => setSubmitError(err.message || 'Apply failed'),
  });

  const runAI = useMutation({
    mutationFn: ({ hintText }) =>
      apiClient.post(`/api/classification/flags/item/${itemId}/reclassify`, {
        useSonnet: true,
        hint_text: hintText || undefined,
      }),
    onSuccess: (data) => {
      if (data?.proposed) {
        setAiProposal(data.proposed);
        setAiError(null);
      } else {
        setAiError('AI returned no proposal (item may already be classified deterministically).');
      }
    },
    onError: (err) => setAiError(err.message || 'AI research failed'),
  });

  const applyAIProposal = useMutation({
    mutationFn: () =>
      apiClient.post(`/api/classification/flags/item/${itemId}/apply-proposal`, {}),
    onSuccess: () => {
      setSuccessMsg('AI proposal applied.');
      setTimeout(() => {
        closeAndReset();
        onUpdated?.();
      }, 800);
    },
    onError: (err) => setSubmitError(err.message || 'Apply failed'),
  });

  const justFlag = useMutation({
    mutationFn: ({ hintText, suggestedLabel }) => {
      const reason = (hintText || '').trim()
        || (suggestedLabel ? `Operator suggests: ${suggestedLabel}` : 'Operator flagged from inline chip');
      const fullHint = [
        hintText || null,
        suggestedLabel ? `Suggested: ${suggestedLabel}` : null,
      ].filter(Boolean).join('\n\n') || null;
      return apiClient.post('/api/classification/flags', {
        item_id: itemId,
        flag_reason: reason.length >= 3 ? reason : 'Operator flagged from inline chip',
        flag_category: 'other',
        flag_severity: 'warning',
        hint_text: fullHint,
      });
    },
    onSuccess: () => {
      setSuccessMsg(isAdmin ? 'Flagged for review.' : 'Suggestion recorded — admin will review.');
      setTimeout(() => {
        closeAndReset();
        onUpdated?.();
      }, 800);
    },
    onError: (err) => setSubmitError(err.message || 'Flag failed'),
  });

  const busy = applyManual.isPending || runAI.isPending
    || applyAIProposal.isPending || justFlag.isPending;

  // ── Render ──────────────────────────────────────────────────────────

  const pct = confidenceToPct(confidence);
  const band = confidenceBand(pct);

  const trigger = (
    <button
      type="button"
      className="cc-chip"
      onClick={() => setOpen((o) => !o)}
      title={pct != null
        ? `${componentName || 'Unclassified'} · ${pct}% confidence — click to correct`
        : 'Unclassified — click to classify'}
      disabled={!itemId}
      data-band={band}
      style={{ '--cc-conf': pct != null ? `${pct}%` : '0%' }}
    >
      <span className="cc-fill" aria-hidden="true" />
      <Tag size={11} className="cc-icon" />
      <span className="cc-name">{componentName || 'Unclassified'}</span>
      <ConfidenceBadge pct={pct} band={band} />
      <Edit3 size={10} className="cc-edit-icon" />
    </button>
  );

  return (
    <span className={`cc cc--${size}${open ? ' cc--open' : ''} ${className}`.trim()}>
      {trigger}

      {open && (
        <div className="cc-panel" role="dialog" aria-label="Correct classification">
          <div className="cc-panel-head">
            <div className="cc-panel-title">
              {isAdmin ? 'Correct this classification' : 'Suggest a correction'}
            </div>
            <button
              type="button"
              className="cc-x"
              onClick={closeAndReset}
              aria-label="Close"
              disabled={busy}
            >
              <X size={14} />
            </button>
          </div>

          <div className="cc-current">
            <span className="cc-label">Current:</span>
            <span className="cc-current-name">{componentName || 'Unclassified'}</span>
            {classificationMethod && <span className="cc-method">{classificationMethod}</span>}
            <ConfidenceBadge pct={pct} band={band} />
          </div>

          {successMsg && (
            <div className="cc-success" role="status">
              <Check size={12} /> {successMsg}
            </div>
          )}

          {aiProposal && !successMsg && (
            <div className="cc-proposal">
              <div className="cc-proposal-head">
                <Sparkles size={14} /> AI proposed
              </div>
              <div className="cc-proposal-body">
                <strong>{aiProposal.component_name || '(no component)'}</strong>
                {(() => {
                  const aiPct = confidenceToPct(aiProposal.confidence);
                  return <ConfidenceBadge pct={aiPct} band={confidenceBand(aiPct)} />;
                })()}
                {aiProposal.method && <span className="cc-method">{aiProposal.method}</span>}
              </div>
              {aiProposal.notes && (
                <details className="cc-reasoning">
                  <summary>Reasoning</summary>
                  <pre>{aiProposal.notes}</pre>
                </details>
              )}
              <div className="cc-actions">
                {isAdmin ? (
                  <button
                    type="button"
                    className="cc-btn cc-btn--primary"
                    onClick={() => applyAIProposal.mutate()}
                    disabled={busy || !aiProposal.component_id}
                  >
                    {applyAIProposal.isPending
                      ? <Loader2 size={12} className="cc-spin" />
                      : <Check size={12} />}
                    Apply AI proposal
                  </button>
                ) : (
                  <span className="cc-note">
                    Only admins can apply directly — use Suggest correction below to record this.
                  </span>
                )}
                <button
                  type="button"
                  className="cc-btn"
                  onClick={() => { setAiProposal(null); setAiError(null); }}
                  disabled={busy}
                >
                  Discard AI proposal
                </button>
              </div>
            </div>
          )}

          {!aiProposal && !successMsg && (
            <>
              <label className="cc-field">
                <span className="cc-label">Correct component (optional)</span>
                <SearchableDropdown
                  type="component"
                  value={pickedComponent?.label || ''}
                  onSelect={(item) => setPickedComponent(item)}
                  placeholder="Search components by name, vendor, or category…"
                />
              </label>

              <label className="cc-field">
                <span className="cc-label">Why is this wrong? What should it be?</span>
                <textarea
                  className="cc-textarea"
                  value={hint}
                  onChange={(e) => setHint(e.target.value.slice(0, 1000))}
                  placeholder="Your reasoning is captured permanently and used as context for future AI classifications of similar items. Be specific — e.g. 'SmartNet is maintenance, not pro services. Pro services = Cisco Advanced Services / CX engagements.'"
                  rows={4}
                  maxLength={1000}
                />
                <div className="cc-charcount">{hint.length}/1000</div>
              </label>

              {submitError && (
                <div className="cc-error">
                  <AlertCircle size={12} /> {submitError}
                </div>
              )}

              <div className="cc-actions">
                {isAdmin ? (
                  <button
                    type="button"
                    className="cc-btn cc-btn--primary"
                    onClick={() => applyManual.mutate({
                      componentId: pickedComponent.id,
                      notes: hint || null,
                    })}
                    disabled={busy || !pickedComponent}
                    title={!pickedComponent
                      ? 'Pick a component first, or use Research with AI / Just flag'
                      : 'Write the correction directly to the item (admin only)'}
                  >
                    {applyManual.isPending
                      ? <Loader2 size={12} className="cc-spin" />
                      : <Check size={12} />}
                    Apply correction
                  </button>
                ) : (
                  <button
                    type="button"
                    className="cc-btn cc-btn--primary"
                    onClick={() => justFlag.mutate({
                      hintText: hint,
                      suggestedLabel: pickedComponent?.label || null,
                    })}
                    disabled={busy || (!hint && !pickedComponent)}
                    title="Record this suggestion for admin review"
                  >
                    {justFlag.isPending
                      ? <Loader2 size={12} className="cc-spin" />
                      : <Flag size={12} />}
                    Suggest correction
                  </button>
                )}

                <button
                  type="button"
                  className="cc-btn"
                  onClick={() => {
                    // Thread the picked component into the prompt so the
                    // agent sees the operator's hypothesis as context.
                    const fullHint = [
                      hint || null,
                      pickedComponent ? `Suggested component: ${pickedComponent.label}` : null,
                    ].filter(Boolean).join('\n\n');
                    runAI.mutate({ hintText: fullHint });
                  }}
                  disabled={busy}
                  title="Send this item to Sonnet with your hint as context (uses Anthropic web search + glossary tools)"
                >
                  {runAI.isPending
                    ? <Loader2 size={12} className="cc-spin" />
                    : <Sparkles size={12} />}
                  Research with AI
                </button>

                {isAdmin && (
                  <button
                    type="button"
                    className="cc-btn cc-btn--ghost"
                    onClick={() => justFlag.mutate({
                      hintText: hint,
                      suggestedLabel: pickedComponent?.label || null,
                    })}
                    disabled={busy || (!hint && !pickedComponent)}
                    title="Record this for later review without changing the classification"
                  >
                    <Flag size={12} /> Just flag
                  </button>
                )}
              </div>

              {aiError && (
                <div className="cc-error">
                  <AlertCircle size={12} /> AI research failed: {aiError}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </span>
  );
}
